import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'

export interface OTPInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
  onComplete?: (code: string) => void
  error?: boolean
  autoFocus?: boolean
  disabled?: boolean
}

/** Multi-cell numeric code input (from the CRM kit): auto-advance, backspace back, paste fills, ←/→. */
export function OTPInput({
  value,
  onChange,
  length = 6,
  onComplete,
  error,
  autoFocus = true,
  disabled = false,
}: OTPInputProps) {
  const inputs = useRef<(HTMLInputElement | null)[]>([])
  const padded = value.padEnd(length, ' ').slice(0, length)

  // Focus once the cells are enabled: a disabled input ignores focus(), and the sign-in shows these cells while the
  // password check is still busy (and disables them again while a code is checked).
  useEffect(() => {
    if (autoFocus && !disabled) inputs.current[Math.min(value.length, length - 1)]?.focus()
  }, [autoFocus, disabled])
  useEffect(() => {
    if (value.length === length) onComplete?.(value)
  }, [value, length, onComplete])

  const setCharAt = (index: number, ch: string) => {
    const arr = padded.split('')
    arr[index] = ch
    onChange(arr.join('').replace(/\s+$/, ''))
  }
  const onInput = (index: number, raw: string) => {
    const ch = raw.replace(/\D/g, '').slice(-1)
    if (!ch) return setCharAt(index, ' ')
    setCharAt(index, ch)
    if (index < length - 1) inputs.current[index + 1]?.focus()
  }
  const onKey = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if ((padded[index] ?? ' ').trim() === '' && index > 0) {
        e.preventDefault()
        setCharAt(index - 1, ' ')
        inputs.current[index - 1]?.focus()
      } else setCharAt(index, ' ')
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault()
      inputs.current[index + 1]?.focus()
    }
  }
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!text) return
    onChange(text)
    setTimeout(() => inputs.current[Math.min(text.length, length - 1)]?.focus(), 0)
  }
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length }).map((_, i) => {
        const ch = padded[i] ?? ' '
        const filled = ch !== ' '
        return (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={filled ? ch : ''}
            onChange={(e) => onInput(i, e.target.value)}
            onKeyDown={(e) => onKey(i, e)}
            onPaste={onPaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            autoComplete="one-time-code"
            aria-label={`Digit ${i + 1}`}
            className={`h-11 w-9 rounded border text-center text-base font-bold text-default outline-none transition disabled:opacity-50 dark:bg-slate-800 ${error ? 'border-red-500 ring-2 ring-red-500/30' : filled ? 'border-primary ring-1 ring-primary/30' : 'border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-slate-600'}`}
          />
        )
      })}
    </div>
  )
}
